import React, { useEffect, useState, useMemo } from 'react';
import SummaryApi from '../common';
import { toast } from 'react-toastify';
import { useTable, usePagination } from 'react-table';
import * as XLSX from 'xlsx';
import { FaFileExcel } from 'react-icons/fa';

const AssignNotInterested = () => {
    const [employees, setEmployees] = useState([]);
    const [loadingEmployees, setLoadingEmployees] = useState(true);
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [studentsData, setStudentsData] = useState([]);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchEmployees = async () => {
        try {
            const res = await fetch(SummaryApi.getEmployeeNames.url, {
                method: SummaryApi.getEmployeeNames.method,
                headers: { 'Authorization': 'Bearer mvm_educational_institutions' },
            });

            const data = await res.json();
            if (data.status) {
                setEmployees(data.data);
            }
        } catch (err) {
            toast.error('Failed to load employees');
        } finally {
            setLoadingEmployees(false);
        }
    };

    useEffect(() => {
        fetchEmployees();
    }, []);

    const fetchStudents = async (employeeId) => {
        setLoading(true);
        try {
            const res = await fetch(SummaryApi.notInterestedStudentsTask.url, {
                method: SummaryApi.notInterestedStudentsTask.method,
                headers: {
                    'Authorization': 'Bearer mvm_educational_institutions',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ employee_id: employeeId }),
            });

            const data = await res.json();
            const students = data?.data || [];

            const updatedStudents = students.map((student, index) => ({
                ...student,
                sno: index + 1,
            }));

            setStudentsData(updatedStudents);
            setFilteredStudents(updatedStudents);
        } catch (error) {
            toast.error(`Error fetching students: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        if (selectedEmployee) {
            fetchStudents(selectedEmployee);
        } else {
            setFilteredStudents([]);
        }
    }, [selectedEmployee]);

    const downloadExcelReport = async () => {
        const studentsToDownload = filteredStudents.filter(s => (s.is_download === '0'));
        if (studentsToDownload.length === 0) {
            toast.info('All students have already been downloaded.');
            return;
        }

        const studentIds = studentsToDownload.map(student => student.student_id);

        // Add SNO and field labels directly
        const filteredData = studentsToDownload.map(student => ({
            SNO: student.sno,
            'Student Name': student.student_name || '',
            'Mobile Number': student.mobile_number1 || ''
        }));

        const worksheet = XLSX.utils.json_to_sheet(filteredData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Students');

        const today = new Date().toISOString().split('T')[0];
        const selectedEmp = employees.find(emp => emp.employee_id === selectedEmployee);
        const employeeName = selectedEmp?.full_name?.replace(/\s+/g, '_') || 'employee';
        const fileName = `${employeeName}_NI_${today}.xlsx`;

        XLSX.writeFile(workbook, fileName);

        try {
            const res = await fetch(SummaryApi.markStudentsAsDownloaded.url, {
                method: SummaryApi.markStudentsAsDownloaded.method,
                headers: {
                    'Authorization': 'Bearer mvm_educational_institutions',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ student_ids: studentIds }),
            });

            const data = await res.json();
            if (data.status) {
                toast.success('Marked as downloaded!');
                fetchStudents(selectedEmployee);
            } else {
                throw new Error(data.message || 'Failed to update download status');
            }
        } catch (error) {
            toast.error(`Failed to mark as downloaded: ${error.message}`);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr || dateStr === 'N/A') return 'N/A';
        const date = new Date(dateStr);
        return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-IN');
    };

    const columns = useMemo(() => [
        { Header: 'SNO', Cell: ({ row }) => row.index + 1 },
        { Header: 'Enrollment Code', accessor: 'enrollment_code' },
        { Header: 'Student Name', accessor: 'student_name' },
        { Header: 'District', accessor: 'district' },
        { Header: 'City', accessor: 'city' },
        { Header: 'College Name', accessor: 'clg_name' },
        { Header: 'Address', accessor: 'address' },
        { Header: 'Mobile Number', accessor: 'mobile_number1' },
        {
            Header: 'Call Duration',
            accessor: 'call_duration',
            Cell: ({ value }) => value?.split('.')?.[0] || value
        },
        { Header: 'Course', accessor: 'course' },
        {
            Header: 'Enrolled Date',
            accessor: 'created_date',
            Cell: ({ value }) => formatDate(value),
        },
        {
            Header: 'Follow Up Date',
            accessor: 'follow_up_date',
            Cell: ({ value }) => formatDate(value),
        }
    ], []);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        state: { pageIndex, pageSize },
        canPreviousPage,
        canNextPage,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        pageCount,
    } = useTable(
        { columns, data: filteredStudents, initialState: { pageIndex: 0 } },
        usePagination
    );

    if (loadingEmployees) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-xl font-semibold text-gray-700">Loading employees...</div>
            </div>
        );
    }

    return (
        <div className="p-4">
            <h2 className="text-xl font-bold mb-4">Assign Not Interested Students</h2>

            <div className='flex justify-between'>
                <div className="mb-4 flex items-center">
                    <label className="font-medium w-full">Select Employee:</label>
                    <select
                        value={selectedEmployee}
                        onChange={(e) => setSelectedEmployee(e.target.value)}
                        className="border px-4 py-2 rounded w-auto ml-4"
                        disabled={loadingEmployees}
                    >
                        <option value="">-- Select Employee --</option>
                        {employees.map((emp) => (
                            <option key={emp.employee_id} value={emp.employee_id}>
                                {emp.full_name}
                            </option>
                        ))}
                    </select>
                </div>

                {filteredStudents.length > 0 && (
                    <div className="mb-4 flex justify-end">
                        <button
                            onClick={downloadExcelReport}
                            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
                        >
                            <div className='flex items-center justify-center gap-2'>
                                <FaFileExcel />
                                <span>Download Excel Report</span>
                            </div>
                        </button>
                    </div>
                )}
            </div>

            <div className="overflow-x-auto">
                <table {...getTableProps()} className="min-w-full mb-4 rounded-lg shadow-md overflow-hidden">
                    <thead className="bg-gray-200">
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <th {...column.getHeaderProps()} className="px-4 py-2 text-left text-gray-700 font-semibold">
                                        {column.render('Header')}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {loading ? (
                            <tr>
                                <td colSpan={columns.length} className="text-center py-8">Loading students...</td>
                            </tr>
                        ) : !selectedEmployee ? (
                            <tr>
                                <td colSpan={columns.length} className="text-center text-red-600 font-semibold py-8">
                                    Please select an employee.
                                </td>
                            </tr>
                        ) : filteredStudents.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="text-center text-gray-600 font-semibold py-8">
                                    No student data found.
                                </td>
                            </tr>
                        ) : (
                            page.map(row => {
                                prepareRow(row);
                                return (
                                    <tr {...row.getRowProps()} className="hover:bg-gray-100 transition duration-200">
                                        {row.cells.map(cell => (
                                            <td {...cell.getCellProps()} className="border px-4 py-2">
                                                {cell.render('Cell')}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {filteredStudents.length > 0 && (
                <div className="flex justify-between items-center mb-4">
                    <span>
                        Page {pageIndex + 1} of {pageCount} of {filteredStudents.length} entries
                    </span>
                    <div>
                        <button onClick={() => gotoPage(0)} disabled={!canPreviousPage} className="px-3 py-1 border rounded">{"<<"}</button>
                        <button onClick={previousPage} disabled={!canPreviousPage} className="px-3 py-1 border rounded">{"<"}</button>
                        <button onClick={nextPage} disabled={!canNextPage} className="px-3 py-1 border rounded">{">"}</button>
                        <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} className="px-3 py-1 border rounded">{">>"}</button>
                    </div>
                    <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} className="ml-4 border rounded">
                        {[5, 10, 20, 50, 100].map(size => (
                            <option key={size} value={size}>Show {size}</option>
                        ))}
                    </select>
                </div>
            )}
        </div>
    );
};

export default AssignNotInterested;
